import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import type { UtilisateurPublic } from '@rh/shared';
import * as api from '../api';
import { obtenirDiffusion, type Diffusion, type MessageOnglets } from '../horsligne/diffusion';
import { ContexteAuth, type EtatAuth, type EtatSession } from './contexte-auth';

/** Le jeton se renouvelle une minute avant son expiration, jamais moins de 5 s après la planification. */
const MARGE_RENOUVELLEMENT_MS = 60_000;
const ATTENTE_MIN_MS = 5_000;
/** Renouvellement en panne réseau : nouveaux essais à 30 s, 1, 2, 4 puis toutes les 5 min. */
const ATTENTES_PANNE_MS = [30_000, 60_000, 120_000, 240_000, 300_000];
/** Démarrage sans réseau : un essai toutes les 30 s. */
const ATTENTE_SANS_RESEAU_MS = 30_000;
/** Retour du réseau ou à l'écran : renouveler aussitôt si le jeton expire d'ici là. */
const SEUIL_RETOUR_MS = 90_000;

interface Instantane {
  utilisateur: UtilisateurPublic | null;
  etat: EtatSession;
  sessionFragile: boolean;
  motifFin: string | null;
  deconnexionDifferee: boolean;
}

/** Tests : diffusion entre onglets et rechargement de la page. */
export interface DependancesAuth {
  diffusion?: Diffusion;
  recharger?: () => void;
}

const INITIAL: Instantane = {
  utilisateur: null,
  etat: 'chargement',
  sessionFragile: false,
  motifFin: null,
  deconnexionDifferee: false,
};

function ecouterEvenement(cible: EventTarget | undefined, type: string, action: () => void): () => void {
  if (!cible) return () => undefined;
  cible.addEventListener(type, action);
  return () => cible.removeEventListener(type, action);
}

/** Même compte, mêmes attributs : on garde l'objet pour ne pas relancer les écrans à chaque renouvellement. */
function memeUtilisateur(a: UtilisateurPublic | null, b: UtilisateurPublic): boolean {
  return a !== null && JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Cycle de vie de la session de l'onglet, hors de React pour rester lisible :
 * reprise au démarrage, minuteur de renouvellement tolérant aux pannes réseau,
 * événements de l'API et des autres onglets. Seul un refus de l'API fait sortir
 * l'utilisateur ; une panne le garde connecté, session « fragile ».
 *
 * Deux compteurs écartent les suites périmées : `generation` change à chaque
 * montage (StrictMode monte deux fois), `epoque` à chaque changement d'état.
 */
class CycleSession {
  private instantane: Instantane = INITIAL;
  private readonly abonnes = new Set<() => void>();
  private minuteur: ReturnType<typeof setTimeout> | null = null;
  private echecs = 0;
  private actif = false;
  private generation = 0;
  private epoque = 0;
  /** Essai de renouvellement (ou de déconnexion) en vol : un seul à la fois. */
  private enVol: object | null = null;
  /** Dernier compte connecté dans cet onglet (oublié à la déconnexion volontaire). */
  private dernierUtilisateurId: string | null = null;
  private nettoyages: Array<() => void> = [];

  constructor(private readonly dependances: DependancesAuth) {}

  readonly abonner = (abonne: () => void): (() => void) => {
    this.abonnes.add(abonne);
    return () => {
      this.abonnes.delete(abonne);
    };
  };

  readonly lire = (): Instantane => this.instantane;

  demarrer(): void {
    this.generation += 1;
    this.actif = true;
    this.nettoyages = [
      api.ecouterSession((evenement) => this.surSession(evenement)),
      this.diffusion().ecouter((message) => this.surMessage(message)),
      ecouterEvenement(globalThis.window, 'online', () => this.surRetour('reseau')),
      ecouterEvenement(globalThis.document, 'visibilitychange', () => {
        if (document.visibilityState === 'visible') this.surRetour('ecran');
      }),
    ];
    switch (this.instantane.etat) {
      case 'chargement':
        void this.demarrage();
        break;
      case 'connecte':
        this.planifierRenouvellement();
        break;
      case 'sans_reseau':
        this.planifier(ATTENTE_SANS_RESEAU_MS, () => void this.reprendre());
        break;
      default:
        break;
    }
  }

  arreter(): void {
    this.generation += 1;
    this.actif = false;
    this.enVol = null;
    this.annulerMinuteur();
    for (const nettoyer of this.nettoyages) nettoyer();
    this.nettoyages = [];
  }

  readonly connecter = async (email: string, motDePasse: string): Promise<void> => {
    const reponse = await api.connexion(email, motDePasse);
    if (this.actif) this.ouvrir(reponse.utilisateur);
  };

  readonly deconnecter = (): Promise<void> => {
    // Appel synchrone : la marque de déconnexion est posée avant toute attente.
    const fin = api.deconnexion();
    this.diffusion().publier({ type: 'session-fermee', motif: 'deconnexion' });
    this.annulerMinuteur();
    this.echecs = 0;
    this.dernierUtilisateurId = null;
    this.maj({ utilisateur: null, etat: 'deconnecte', sessionFragile: false, motifFin: null, deconnexionDifferee: false });
    const { generation, epoque } = this;
    return fin.then((issue) => {
      if (generation === this.generation && epoque === this.epoque) {
        this.maj({ deconnexionDifferee: issue === 'en_attente' });
      }
    });
  };

  readonly reessayer = (): void => {
    const { etat, sessionFragile, deconnexionDifferee } = this.instantane;
    if (etat === 'sans_reseau') void this.reprendre();
    else if (etat === 'connecte' && sessionFragile) void this.renouvelerConnecte();
    else if (etat === 'deconnecte' && deconnexionDifferee) void this.terminerDeconnexion();
  };

  // -------------------------------------------------------------------------

  private diffusion(): Diffusion {
    return this.dependances.diffusion ?? obtenirDiffusion();
  }

  private recharger(): void {
    if (this.dependances.recharger) this.dependances.recharger();
    else window.location.reload();
  }

  private maj(partiel: Partial<Instantane>): void {
    const suivant = { ...this.instantane, ...partiel };
    const cles = Object.keys(suivant) as Array<keyof Instantane>;
    if (cles.every((cle) => suivant[cle] === this.instantane[cle])) return;
    if (suivant.etat !== this.instantane.etat) {
      this.epoque += 1;
      this.enVol = null;
    }
    this.instantane = suivant;
    for (const abonne of [...this.abonnes]) abonne();
  }

  private planifier(attente: number, action: () => void): void {
    this.annulerMinuteur();
    if (!this.actif) return;
    this.minuteur = setTimeout(() => {
      this.minuteur = null;
      action();
    }, attente);
  }

  private annulerMinuteur(): void {
    if (this.minuteur !== null) clearTimeout(this.minuteur);
    this.minuteur = null;
  }

  private planifierRenouvellement(): void {
    const attente = Math.max(api.jetonExpireDans() - MARGE_RENOUVELLEMENT_MS, ATTENTE_MIN_MS);
    this.planifier(attente, () => void this.renouvelerConnecte());
  }

  private ouvrir(utilisateur: UtilisateurPublic): void {
    this.echecs = 0;
    this.dernierUtilisateurId = utilisateur.id;
    this.maj({
      utilisateur: memeUtilisateur(this.instantane.utilisateur, utilisateur) ? this.instantane.utilisateur : utilisateur,
      etat: 'connecte',
      sessionFragile: false,
      motifFin: null,
      deconnexionDifferee: false,
    });
    this.diffusion().publier({ type: 'session-ouverte', utilisateurId: utilisateur.id });
    this.planifierRenouvellement();
  }

  /** Refus de l'API : l'onglet sort, les autres sont prévenus. Aucune saisie du téléphone n'est effacée ici. */
  private perdre(message: string | null, code: string | null): void {
    this.annulerMinuteur();
    this.echecs = 0;
    this.maj({ utilisateur: null, etat: 'session_perdue', sessionFragile: false, motifFin: message });
    this.diffusion().publier({ type: 'session-fermee', motif: 'perdue', ...(code ? { code } : {}) });
  }

  private surRenouvelee(utilisateur: UtilisateurPublic): void {
    this.echecs = 0;
    this.maj({
      utilisateur: memeUtilisateur(this.instantane.utilisateur, utilisateur) ? this.instantane.utilisateur : utilisateur,
      sessionFragile: false,
    });
    this.planifierRenouvellement();
  }

  /**
   * Suite d'un essai devenue sans objet (état changé pendant le vol) : un jeton
   * obtenu malgré tout ne reste pas en mémoire d'un onglet qui n'est plus connecté.
   */
  private suitePerimee(generation: number, epoque: number, issue: api.IssueRenouvellement): boolean {
    if (generation !== this.generation) return true;
    if (epoque === this.epoque) return false;
    if (issue.etat === 'renouvele' && this.instantane.etat !== 'connecte') api.memoriserSession(null);
    return true;
  }

  private async demarrage(): Promise<void> {
    const { generation } = this;
    if (api.deconnexionEnAttente()) {
      // Le cookie httpOnly, que le JavaScript ne peut effacer, rouvrirait la
      // session du compte sorti : on termine la déconnexion, sans renouvellement.
      const issue = await api.deconnexion();
      if (generation !== this.generation || this.instantane.etat !== 'chargement') return;
      this.maj({ utilisateur: null, etat: 'deconnecte', deconnexionDifferee: issue === 'en_attente' });
      return;
    }
    await this.reprendre();
  }

  /** Reprise sans mot de passe : au démarrage, sans réseau, ou quand un autre onglet ouvre la session. */
  private async reprendre(): Promise<void> {
    if (this.enVol || this.instantane.etat === 'connecte') return;
    const vol = {};
    this.enVol = vol;
    this.annulerMinuteur();
    const { generation, epoque } = this;
    const issue = await api.renouveler();
    if (this.enVol === vol) this.enVol = null;
    if (this.suitePerimee(generation, epoque, issue)) return;

    const { etat } = this.instantane;
    const auDemarrage = etat === 'chargement' || etat === 'sans_reseau';
    switch (issue.etat) {
      case 'renouvele':
        this.ouvrir(issue.reponse.utilisateur);
        break;
      case 'hors_ligne':
        if (auDemarrage) {
          this.maj({ etat: 'sans_reseau' });
          this.planifier(ATTENTE_SANS_RESEAU_MS, () => void this.reprendre());
        }
        break;
      case 'refuse':
        if (!auDemarrage) break;
        // Session refusée par l'API (révoquée, expirée, compte inactif) : l'écran de
        // connexion le dit et compte les saisies gardées. Sans session connue du
        // serveur (premier passage, cookie absent), simple écran de connexion.
        if (issue.code !== null && issue.code !== 'SESSION_INVALIDE') {
          this.maj({ utilisateur: null, etat: 'session_perdue', motifFin: issue.message });
        } else {
          this.maj({ utilisateur: null, etat: 'deconnecte' });
        }
        break;
    }
  }

  /** Minuteur tolérant : une panne garde l'utilisateur et replanifie ; seul un refus le fait sortir. */
  private async renouvelerConnecte(): Promise<void> {
    if (this.enVol || this.instantane.etat !== 'connecte') return;
    const vol = {};
    this.enVol = vol;
    this.annulerMinuteur();
    const { generation, epoque } = this;
    const issue = await api.renouveler();
    if (this.enVol === vol) this.enVol = null;
    if (this.suitePerimee(generation, epoque, issue)) return;

    switch (issue.etat) {
      case 'renouvele':
        // Autre compte : l'événement `autre_utilisateur` recharge déjà la page.
        if (issue.reponse.utilisateur.id === this.instantane.utilisateur?.id) {
          this.surRenouvelee(issue.reponse.utilisateur);
        }
        break;
      case 'hors_ligne': {
        this.echecs += 1;
        this.maj({ sessionFragile: true });
        const attente = ATTENTES_PANNE_MS[Math.min(this.echecs, ATTENTES_PANNE_MS.length) - 1]!;
        this.planifier(attente, () => void this.renouvelerConnecte());
        break;
      }
      case 'refuse':
        this.perdre(issue.message, issue.code);
        break;
    }
  }

  /** Déconnexion restée en attente : la ferme sur le serveur, sauf si une connexion l'a déjà levée. */
  private async terminerDeconnexion(): Promise<void> {
    if (this.enVol) return;
    if (!api.deconnexionEnAttente()) {
      this.maj({ deconnexionDifferee: false });
      return;
    }
    const vol = {};
    this.enVol = vol;
    const { generation, epoque } = this;
    const issue = await api.deconnexion();
    if (this.enVol === vol) this.enVol = null;
    if (generation !== this.generation || epoque !== this.epoque) return;
    this.maj({ deconnexionDifferee: issue === 'en_attente' });
  }

  private surRetour(source: 'reseau' | 'ecran'): void {
    const { etat, sessionFragile, deconnexionDifferee } = this.instantane;
    if (etat === 'sans_reseau') {
      void this.reprendre();
    } else if (etat === 'connecte') {
      if ((source === 'reseau' && sessionFragile) || api.jetonExpireDans() < SEUIL_RETOUR_MS) {
        void this.renouvelerConnecte();
      }
    } else if (etat === 'deconnecte' && deconnexionDifferee) {
      void this.terminerDeconnexion();
    }
  }

  private surSession(evenement: api.EvenementSession): void {
    const { etat, utilisateur } = this.instantane;
    switch (evenement.type) {
      case 'renouvelee':
        // Renouvellement fait ailleurs dans l'onglet (appel en 401) : le minuteur repart du nouveau jeton.
        if (etat === 'connecte' && evenement.utilisateur.id === utilisateur?.id) {
          this.surRenouvelee(evenement.utilisateur);
        }
        break;
      case 'perdue':
        // Hors état connecté, c'est la reprise en cours qui conclut.
        if (etat === 'connecte') this.perdre(evenement.message, evenement.code);
        break;
      case 'autre_utilisateur':
        this.recharger();
        break;
    }
  }

  private surMessage(message: MessageOnglets): void {
    const { etat, utilisateur } = this.instantane;
    if (message.type === 'session-fermee') {
      if (etat === 'deconnecte' || (message.motif === 'perdue' && etat === 'session_perdue')) return;
      // Le motif règle les brouillons de cet onglet : gardés sur une session refusée, plus rien sinon.
      api.oublierSession(message.motif, message.code ?? null);
      this.annulerMinuteur();
      this.echecs = 0;
      if (message.motif === 'deconnexion') {
        this.dernierUtilisateurId = null;
        this.maj({ utilisateur: null, etat: 'deconnecte', sessionFragile: false, motifFin: null, deconnexionDifferee: false });
      } else {
        this.maj({ utilisateur: null, etat: 'session_perdue', sessionFragile: false, motifFin: null });
      }
    } else if (message.type === 'session-ouverte') {
      if (etat === 'connecte') {
        // Le cookie appartient désormais à un autre compte : cet onglet repart de zéro.
        if (message.utilisateurId !== utilisateur?.id) this.recharger();
      } else if (etat !== 'chargement') {
        if (this.dernierUtilisateurId === null || this.dernierUtilisateurId === message.utilisateurId) {
          void this.reprendre();
        }
      }
    }
  }
}

export function FournisseurAuth({ enfants, dependances }: { enfants: ReactNode; dependances?: DependancesAuth }) {
  const [cycle] = useState(() => new CycleSession(dependances ?? {}));
  const instantane = useSyncExternalStore(cycle.abonner, cycle.lire);

  useEffect(() => {
    cycle.demarrer();
    return () => cycle.arreter();
  }, [cycle]);

  const valeur = useMemo<EtatAuth>(
    () => ({
      ...instantane,
      chargement: instantane.etat === 'chargement',
      connecter: cycle.connecter,
      deconnecter: cycle.deconnecter,
      reessayer: cycle.reessayer,
    }),
    [instantane, cycle],
  );

  return <ContexteAuth.Provider value={valeur}>{enfants}</ContexteAuth.Provider>;
}
